import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function Account() {
  const { user, refreshUser } = useAuth();
  const normalizeLiInput = (val) => {
    const cleaned = val.replace(/\s+/g, '');
    const m = cleaned.match(/linkedin\.com\/in\/([a-zA-Z0-9._-]+)\/?/);
    if (m) return m[1];
    return cleaned.replace(/^@/, '').replace(/\/$/, '');
  };
  const initLinkedin = user?.linkedin || '';
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', linkedin: initLinkedin, college: user?.college || '', year: user?.year || '' });
  const resolvedLiUsername = (() => {
    const v = form.linkedin.replace(/\s+/g, '');
    const m = v.match(/linkedin\.com\/in\/([a-zA-Z0-9._-]+)\/?/);
    return m ? m[1] : v.replace(/^@/, '').replace(/\/$/, '');
  })();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [txns, setTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    api.get('/user/tokens?limit=20')
      .then(({ data }) => setTxns(data.txns))
      .catch(() => {})
      .finally(() => setTxnsLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/user/profile', form);
      await refreshUser();
      setSuccess('Profile updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteData = async () => {
    if (!window.confirm('This will permanently delete all your chat history and resumes. This cannot be undone. Continue?')) return;
    setDeleting(true); setDeleteError(''); setDeleteSuccess(false);
    try {
      await api.delete('/user/data');
      setDeleteSuccess(true);
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete data. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const initials = (user?.name?.[0] || user?.phone?.slice(-2) || 'U').toUpperCase();
  const balance = user?.tokenBalance ?? 0;
  const balanceLow = balance < 2000;
  const balanceEmpty = balance <= 0;

  return (
    <div style={s.page} className="page-content">
      {/* Profile header card */}
      <div style={s.profileCard}>
        <div style={s.profileBg} />
        <div style={s.profileContent}>
          <div style={s.avatar}>{initials}</div>
          <div style={s.profileInfo}>
            <h1 style={s.profileName}>{user?.name || 'No name yet'}</h1>
            <p style={s.profilePhone}>{user?.phone}</p>
            <span style={s.roleBadge}>{user?.role}</span>
          </div>
        </div>
      </div>

      {/* Token balance card */}
      <div style={{ ...s.tokenCard, borderColor: balanceEmpty ? 'rgba(185,28,28,0.3)' : balanceLow ? 'rgba(217,119,6,0.3)' : 'var(--gold-mid)' }}>
        <div style={s.tokenLeft}>
          <div style={{ ...s.tokenIconWrap, background: balanceEmpty ? 'var(--danger-bg)' : 'var(--gold-light)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={balanceEmpty ? 'var(--danger)' : 'var(--gold)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div>
            <p style={s.tokenLabel}>Token Balance</p>
            <p style={{ ...s.tokenValue, color: balanceEmpty ? 'var(--danger)' : balanceLow ? 'var(--warning)' : 'var(--navy)' }}>
              {balance.toLocaleString()}
            </p>
          </div>
        </div>
        <div style={s.tokenRight}>
          {balanceEmpty && <span style={s.tokenBadgeDanger}>Exhausted</span>}
          {balanceLow && !balanceEmpty && <span style={s.tokenBadgeWarn}>Low</span>}
          {!balanceLow && <span style={s.tokenBadgeOk}>Available</span>}
        </div>
      </div>

      {balanceEmpty && (
        <div style={s.exhaustedNote}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Your token balance is exhausted. You cannot send new queries until an admin grants you more tokens.
        </div>
      )}

      {/* Stats row */}
      <div style={s.statsRow}>
        {[
          { label: 'Member since', value: user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Today', icon: '📅' },
          { label: 'Queries', value: (user?.totalQueries || 0).toLocaleString(), icon: '💬' },
          { label: 'Tokens used', value: (user?.tokensUsed || 0).toLocaleString(), icon: '⚡' },
        ].map((stat) => (
          <div key={stat.label} style={s.statCard}>
            <span style={s.statIcon}>{stat.icon}</span>
            <div>
              <p style={s.statLabel}>{stat.label}</p>
              <p style={s.statVal}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Edit form */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}>Edit Profile</h2>
          <p style={s.cardSub}>Update your display name and email</p>
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Display Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              style={s.input}
              className="nice-input"
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              style={s.input}
              className="nice-input"
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>LinkedIn Profile</label>
            <input
              type="text"
              value={form.linkedin}
              onChange={(e) => setForm((f) => ({ ...f, linkedin: normalizeLiInput(e.target.value) }))}
              placeholder="linkedin.com/in/username or just username"
              style={s.input}
              className="nice-input"
            />
            {resolvedLiUsername && (
              <a
                href={`https://linkedin.com/in/${resolvedLiUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '11px', color: 'var(--navy-2)', marginTop: '2px', textDecoration: 'none', borderBottom: '1px dashed var(--border)' }}
              >
                linkedin.com/in/{resolvedLiUsername}
              </a>
            )}
          </div>

          <div style={s.field}>
            <label style={s.label}>Law College</label>
            <input
              value={form.college}
              onChange={(e) => setForm((f) => ({ ...f, college: e.target.value }))}
              placeholder="e.g. Government Law College, Mumbai"
              style={s.input}
              className="nice-input"
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Year of Study</label>
            <select
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
              style={s.input}
            >
              <option value="">Select year</option>
              <option value="1st">1st Year</option>
              <option value="2nd">2nd Year</option>
              <option value="3rd">3rd Year</option>
              <option value="4th">4th Year</option>
              <option value="5th">5th Year</option>
            </select>
          </div>

          {error && <div style={s.errorBox}>{error}</div>}
          {success && <div style={s.successBox}>{success}</div>}

          <button type="submit" disabled={saving} style={s.btn} className="primary-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Account info */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}>Account Details</h2>
        </div>
        <div style={s.infoList}>
          {[
            { label: 'Phone Number', value: user?.phone },
            { label: 'Role', value: user?.role },
            { label: 'Joined', value: user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-' },
          ].map(({ label, value }) => (
            <div key={label} style={s.infoRow}>
              <span style={s.infoLabel}>{label}</span>
              <span style={s.infoVal}>{value || '-'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Token transaction history */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}>Token History</h2>
          <p style={s.cardSub}>Your last 20 token credits and usage</p>
        </div>
        {txnsLoading ? (
          <p style={s.emptyText}>Loading...</p>
        ) : txns.length === 0 ? (
          <p style={s.emptyText}>No token transactions yet.</p>
        ) : (
          <div style={s.txnList}>
            {txns.map((t) => (
              <div key={t._id} style={s.txnRow}>
                <div style={{ ...s.txnIcon, background: t.type === 'credit' ? 'var(--success-bg)' : 'var(--danger-bg)', color: t.type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                  {t.type === 'credit' ? '+' : '−'}
                </div>
                <div style={s.txnMid}>
                  <p style={s.txnTitle}>
                    {t.source === 'admin_grant' && 'Admin grant'}
                    {t.source === 'purchase' && 'Purchase'}
                    {t.source === 'chat_usage' && 'Query usage'}
                  </p>
                  {t.note && <p style={s.txnNote}>{t.note}</p>}
                  {t.source === 'admin_grant' && t.addedBy && (
                    <p style={s.txnNote}>by {t.addedBy.name || t.addedBy.phone}</p>
                  )}
                </div>
                <div style={s.txnRight}>
                  <p style={{ ...s.txnAmount, color: t.type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                    {t.type === 'credit' ? '+' : '−'}{t.amount.toLocaleString()}
                  </p>
                  <p style={s.txnBalance}>{t.balanceAfter.toLocaleString()} after</p>
                  <p style={s.txnDate}>{new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div style={s.dangerCard}>
        <div style={s.dangerHeader}>
          <h2 style={s.dangerTitle}>Delete My Data</h2>
          <p style={s.dangerSub}>Permanently delete all your chat history and resumes. This cannot be undone.</p>
        </div>
        <div style={s.dangerBody}>
          {deleteSuccess ? (
            <div style={s.successBox}>All your chat history and resumes have been deleted.</div>
          ) : (
            <>
              {deleteError && <div style={s.errorBox}>{deleteError}</div>}
              <button onClick={handleDeleteData} disabled={deleting} style={s.dangerBtn}>
                {deleting ? 'Deleting...' : 'Delete All Data'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { maxWidth: '600px', margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '16px' },
  profileCard: { borderRadius: '16px', overflow: 'hidden', background: '#fff', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', position: 'relative' },
  profileBg: { height: '80px', background: 'var(--navy)' },
  profileContent: { padding: '0 20px 20px', display: 'flex', alignItems: 'flex-end', gap: '16px', marginTop: '-32px' },
  avatar: {
    width: '64px', height: '64px', borderRadius: '50%',
    background: 'var(--navy-2)', color: 'var(--gold-mid)',
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '24px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '3px solid #fff', flexShrink: 0, boxShadow: 'var(--shadow)',
  },
  profileInfo: { paddingTop: '36px' },
  profileName: { fontSize: '18px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' },
  profilePhone: { fontSize: '13px', color: 'var(--text-3)', marginBottom: '6px' },
  roleBadge: {
    background: 'var(--gold-light)', color: 'var(--gold)',
    fontSize: '11px', fontWeight: '700', padding: '3px 10px',
    borderRadius: '99px', textTransform: 'capitalize',
    border: '1px solid var(--gold-mid)',
  },

  tokenCard: {
    background: '#fff', border: '1px solid', borderRadius: '14px',
    padding: '16px 20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', boxShadow: 'var(--shadow-sm)',
  },
  tokenLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  tokenIconWrap: { width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tokenLabel: { fontSize: '11px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' },
  tokenValue: { fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' },
  tokenRight: {},
  tokenBadgeDanger: { background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '99px' },
  tokenBadgeWarn: { background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '99px' },
  tokenBadgeOk: { background: 'var(--success-bg)', color: 'var(--success)', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '99px' },

  exhaustedNote: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: 'var(--danger-bg)', color: 'var(--danger)',
    border: '1px solid rgba(185,28,28,0.2)', borderRadius: '12px',
    padding: '12px 16px', fontSize: '13px', lineHeight: '1.5',
  },

  statsRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  statCard: { flex: '1 1 120px', background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '14px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: 'var(--shadow-sm)' },
  statIcon: { fontSize: '20px' },
  statLabel: { fontSize: '10px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statVal: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)', textTransform: 'capitalize', marginTop: '2px' },

  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '18px 20px 0' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' },
  cardSub: { fontSize: '12px', color: 'var(--text-3)', marginBottom: '0' },
  form: { padding: '14px 20px 20px', display: 'flex', flexDirection: 'column', gap: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '600', color: 'var(--text-2)' },
  optional: { color: 'var(--text-3)', fontWeight: '400' },
  input: { padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', background: 'var(--bg)', color: 'var(--text)' },
  errorBox: { background: 'var(--danger-bg)', border: '1px solid rgba(185,28,28,0.2)', color: 'var(--danger)', padding: '10px 14px', borderRadius: '10px', fontSize: '13px' },
  successBox: { background: 'var(--success-bg)', border: '1px solid rgba(26,102,64,0.2)', color: 'var(--success)', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: '600' },
  btn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },

  infoList: { padding: '10px 20px 16px', display: 'flex', flexDirection: 'column' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--border-2)' },
  infoLabel: { fontSize: '13px', color: 'var(--text-3)', fontWeight: '500' },
  infoVal: { fontSize: '13px', fontWeight: '700', color: 'var(--navy)', textTransform: 'capitalize' },

  dangerCard:   { background: '#fff', border: '1px solid rgba(185,28,28,0.25)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  dangerHeader: { padding: '18px 20px 0' },
  dangerTitle:  { fontSize: '15px', fontWeight: '700', color: 'var(--danger)', marginBottom: '2px' },
  dangerSub:    { fontSize: '12px', color: 'var(--text-3)', marginBottom: '0' },
  dangerBody:   { padding: '14px 20px 20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  dangerBtn:    { background: 'var(--danger)', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-start' },

  emptyText: { padding: '20px', color: 'var(--text-3)', fontSize: '13px' },
  txnList: { display: 'flex', flexDirection: 'column' },
  txnRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px', borderBottom: '1px solid var(--border-2)' },
  txnIcon: { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '800', flexShrink: 0 },
  txnMid: { flex: 1, minWidth: 0 },
  txnTitle: { fontSize: '13px', fontWeight: '600', color: 'var(--navy)' },
  txnNote: { fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  txnRight: { textAlign: 'right', flexShrink: 0 },
  txnAmount: { fontSize: '14px', fontWeight: '800' },
  txnBalance: { fontSize: '11px', color: 'var(--text-3)', marginTop: '1px' },
  txnDate: { fontSize: '10px', color: 'var(--text-3)', marginTop: '1px' },
};
